import { forwardRef, ButtonHTMLAttributes } from 'react';

/**
 * The design's buttons (OO1): 4px corners, semibold Inter, no shadow, KW red for the one
 * primary action on a view, a white hairline button for everything else, dark for the
 * "Invite a party" kind of secondary commitment. Hover darkens one step; focus is the KW
 * 3px red ring; disabled is 40% opacity - all per the design system's States section.
 */
type ButtonVariant = 'primary' | 'secondary' | 'outline' | 'ghost' | 'danger';
type ButtonSize = 'sm' | 'md' | 'lg';

interface ButtonProps extends ButtonHTMLAttributes<HTMLButtonElement> {
  variant?: ButtonVariant;
  size?: ButtonSize;
  isLoading?: boolean;
  leftIcon?: React.ReactNode;
  rightIcon?: React.ReactNode;
}

const variantStyles: Record<ButtonVariant, string> = {
  primary:
    'bg-primary-500 text-white hover:bg-primary-600 active:bg-primary-700',
  secondary:
    'bg-gray-900 text-white hover:bg-gray-800 active:bg-gray-950',
  outline:
    'border border-gray-200 bg-white text-gray-800 hover:border-gray-900 active:bg-gray-50',
  ghost:
    'text-gray-700 hover:bg-gray-100 hover:text-gray-900',
  // The design system's danger IS the brand red; a second red would be a new hue.
  danger:
    'bg-primary-500 text-white hover:bg-primary-600 active:bg-primary-700',
};

const sizeStyles: Record<ButtonSize, string> = {
  sm: 'h-[30px] px-3 text-[11.5px]',
  md: 'h-9 px-3.5 text-[12.5px]',
  lg: 'h-10 px-4 text-[13.5px]',
};

export const Button = forwardRef<HTMLButtonElement, ButtonProps>(
  (
    {
      variant = 'primary',
      size = 'md',
      isLoading = false,
      leftIcon,
      rightIcon,
      className = '',
      disabled,
      children,
      ...props
    },
    ref
  ) => {
    return (
      <button
        ref={ref}
        disabled={disabled || isLoading}
        className={`
          inline-flex items-center justify-center gap-1.5 whitespace-nowrap font-semibold leading-none rounded-lg
          transition-colors duration-150 ease-out
          focus:outline-none focus-visible:ring-[3px] focus-visible:ring-primary-500/25
          disabled:opacity-40 disabled:cursor-not-allowed
          ${variantStyles[variant]}
          ${sizeStyles[size]}
          ${className}
        `}
        {...props}
      >
        {isLoading ? (
          <svg
            className="animate-spin -ml-1 mr-2 h-4 w-4"
            fill="none"
            viewBox="0 0 24 24"
          >
            <circle
              className="opacity-25"
              cx="12"
              cy="12"
              r="10"
              stroke="currentColor"
              strokeWidth="4"
            />
            <path
              className="opacity-75"
              fill="currentColor"
              d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
            />
          </svg>
        ) : (
          leftIcon && <span className="-ml-0.5">{leftIcon}</span>
        )}
        {children}
        {!isLoading && rightIcon && <span className="-mr-0.5">{rightIcon}</span>}
      </button>
    );
  }
);

Button.displayName = 'Button';
